import { createStore } from "zustand/vanilla";
import { conflict, err, ok } from "@pack-it-up/shared";
import { computeTemplateSyncDraft, planTemplateSync, syncCounts, syncEntryKey, } from "@pack-it-up/core";
import { errorToMessage } from "../errorToMessage.js";
// Doc 13 §7 — the dropdown may only offer templates the sync can actually write:
// imported by this trip, mine, unarchived, and held as owner/editor. `TemplateSummary`
// already carries `role`, so no extra document read is needed.
export const syncCandidates = (trip, mine) => mine.filter((t) => !t.archived && (t.role === "owner" || t.role === "editor") && trip.importedTemplateIds.includes(t.id));
// The rows a category checkbox cascades over: trip rows in that trip category, plus
// template-only rows in the matched template category.
const entriesUnderCategory = (draft, category, tripContents, templateContents) => {
    const tripCategoryId = category.tripRowId;
    const templateCategoryId = category.templateRowId;
    const tripRowIds = new Set();
    const templateRowIds = new Set();
    if (tripCategoryId != null)
        for (const r of [...tripContents.items, ...tripContents.todos])
            if (r.categoryId === tripCategoryId)
                tripRowIds.add(r.id);
    if (templateCategoryId != null)
        for (const r of [...templateContents.items, ...templateContents.todos])
            if (r.categoryId === templateCategoryId)
                templateRowIds.add(r.id);
    return draft.entries.filter((e) => {
        if (e.collection === "categories")
            return false;
        if (e.tripRowId != null)
            return tripRowIds.has(e.tripRowId);
        return e.templateRowId != null && templateRowIds.has(e.templateRowId);
    });
};
const seedChecked = (draft) => {
    const out = new Set();
    for (const e of draft.entries)
        if (e.defaultChecked)
            out.add(syncEntryKey(e));
    return out;
};
export const createTemplateSyncStore = (deps) => {
    // The trip and the two row sets the planners need. Not UI state — kept out of the
    // store's shape so a re-render never depends on them.
    let trip = null;
    let tripContents = { categories: [], items: [], todos: [] };
    let templateContents = { categories: [], items: [], todos: [] };
    let mine = [];
    // Stream subscriptions are owned by the store: dispose() unsubscribes them all, so a
    // superseded store stops writing into state nothing renders. `subs` is filled after
    // the store exists (the streams push into it).
    const subs = [];
    const closedState = () => ({
        open: false,
        templateId: null,
        candidates: [],
        draft: null,
        checked: new Set(),
        loading: false,
        applying: false,
        error: null,
    });
    const store = createStore((set, get) => ({
        ...closedState(),
        summary: null,
        counts() {
            const { draft, checked } = get();
            return draft ? syncCounts(draft, checked) : { updates: 0, adds: 0, removes: 0, conflicts: 0 };
        },
        categoryTickState(key) {
            const { draft, checked } = get();
            const category = draft?.entries.find((e) => e.collection === "categories" && syncEntryKey(e) === key);
            if (!draft || !category)
                return "none";
            const rows = entriesUnderCategory(draft, category, tripContents, templateContents);
            if (rows.length === 0)
                return checked.has(key) ? "all" : "none";
            const ticked = rows.filter((e) => checked.has(syncEntryKey(e))).length;
            if (ticked === 0)
                return "none";
            return ticked === rows.length ? "all" : "some";
        },
        openSync(nextTrip, contents) {
            trip = nextTrip;
            tripContents = contents;
            templateContents = { categories: [], items: [], todos: [] };
            set({ ...closedState(), open: true, candidates: syncCandidates(nextTrip, mine), summary: null });
        },
        async selectTemplate(templateId) {
            if (trip == null)
                return;
            set({ templateId, loading: true, error: null, draft: null, checked: new Set() });
            const r = await deps.templates.fetchTemplateRows(templateId);
            if (!r.ok) {
                templateContents = { categories: [], items: [], todos: [] };
                set({ loading: false, error: errorToMessage(r.error) });
                return;
            }
            templateContents = r.value;
            const draft = computeTemplateSyncDraft(templateId, trip, tripContents, templateContents);
            set({ draft, checked: seedChecked(draft), loading: false, error: null });
        },
        toggle(key) {
            const next = new Set(get().checked);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            set({ checked: next });
        },
        toggleCategory(key) {
            const { draft, checked } = get();
            const category = draft?.entries.find((e) => e.collection === "categories" && syncEntryKey(e) === key);
            if (!draft || !category)
                return;
            const rows = entriesUnderCategory(draft, category, tripContents, templateContents);
            const on = get().categoryTickState(key) !== "all";
            const next = new Set(checked);
            for (const e of [category, ...rows]) {
                const k = syncEntryKey(e);
                if (on)
                    next.add(k);
                else
                    next.delete(k);
            }
            set({ checked: next });
        },
        checkAll() {
            const { draft } = get();
            if (!draft)
                return;
            set({ checked: new Set(draft.entries.map(syncEntryKey)) });
        },
        uncheckAll() {
            set({ checked: new Set() });
        },
        async confirm() {
            const { draft, checked, templateId } = get();
            if (!draft || !trip || templateId == null) {
                const error = conflict("No template selected.");
                set({ error: errorToMessage(error) });
                return err(error);
            }
            set({ applying: true, error: null });
            const plan = planTemplateSync(draft, trip, tripContents, templateContents, checked);
            const counts = syncCounts(draft, checked);
            const r = await deps.applyTemplateSyncPlan(plan);
            if (!r.ok) {
                // Mode stays open, ticks intact — the user can retry without re-deciding.
                set({ applying: false, error: errorToMessage(r.error) });
                return r;
            }
            const summary = {
                templateId,
                templateName: get().candidates.find((t) => t.id === templateId)?.name ?? "",
                counts,
            };
            set({ ...closedState(), summary });
            return ok(summary);
        },
        cancel() {
            trip = null;
            tripContents = { categories: [], items: [], todos: [] };
            templateContents = { categories: [], items: [], todos: [] };
            set({ ...closedState(), summary: null });
        },
        dispose() {
            for (const unsubscribe of subs.splice(0))
                unsubscribe();
        },
    }));
    subs.push(deps.templates.observeMyTemplates(deps.userId, { archived: false }).subscribe((templates) => {
        mine = templates;
        const { open } = store.getState();
        if (open && trip)
            store.setState({ candidates: syncCandidates(trip, templates) });
    }));
    return store;
};
//# sourceMappingURL=templateSyncStore.js.map